import { Component, ViewChild, ElementRef, OnDestroy, OnInit } from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { Papa } from 'ngx-papaparse';
import { map } from 'lodash';
import { MatDialogRef } from '@angular/material/dialog';

import {
  IPHelper,
  CommonRegEx,
  NotificationService,
  SitesService,
  UnmanagedDevicesService
} from '@nrwl/common-lib';

import { LABELS } from './types';
import {
  SiteRelatedCSVFields,
  LocationRelatedCSVFields
} from '../unmanaged-device-list/unmanaged-device-list.component';

@Component({
  selector: 'nrwl-import-devices',
  templateUrl: './import.devices.component.html',
  styleUrls: ['./import.devices.component.scss']
})
export class ImportDevicesComponent implements OnDestroy, OnInit {
  @ViewChild('file') file: ElementRef<HTMLInputElement>;
  validationErrors = [];
  isImportFailed = false;
  importFailedMsg = null;
  importStatusMsg = '';
  importProgress = 0;
  numberOfItemsPerIteration = 250;
  uploadInProgress = false;
  addDevicesList = [];
  updateDevicesList = [];
  deleteDevicesList = [];
  updateProcessed = false;
  addProcessed = false;
  deleteProcessed = false;
  badFileFormat = false;
  agentsCount: number;
  unmanagedDeviceList = [];
  public files: Set<File> = new Set();
  isLoading = true;
  uploading = false;
  reflectorsIpList = [];
  loopbacksMacList = [];
  loopbacksIpList = [];
  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  progress: any;

  showClearButton = false;
  fileName = '';
  lockCode = '';
  errorRetrievalMsg = '';
  devicesFiles: FileList;
  defaultValue = {
    action: 'include',
    latitude: 0,
    longitude: 0,
    twampipv6: 'true',
    lightipv6: 'true',
    type: 'unmanaged'
  };
  sites = [];
  private readonly unsubscribe: Subject<void> = new Subject();
  private cacheObj: Subscription;

  constructor(
    private papa: Papa,
    private dialogRef: MatDialogRef<ImportDevicesComponent>,
    private unmanagedDevicesService: UnmanagedDevicesService,
    private readonly notifyService: NotificationService,
    private sitesService: SitesService
  ) {}

  ngOnInit() {
    this.getAllDevices();
  }

  ngOnDestroy() {
    if (this.cacheObj) {
      this.cacheObj.unsubscribe();
    }
    if (this.unsubscribe) {
      this.unsubscribe.next();
      this.unsubscribe.complete();
    }
  }

  onFilesAdded(): void {
    this.badFileFormat = false;
    this.validationErrors = [];
    this.importFailedMsg = null;
    this.devicesFiles = this.file.nativeElement.files;
    const tempFileNameList = [];

    for (let i = 0; i < this.devicesFiles.length; ++i) {
      this.files.add(this.devicesFiles[i]);
      tempFileNameList.push(this.devicesFiles[i].name);
    }

    this.fileName = tempFileNameList.join(', ');
    this.showClearButton = true;

    if (this.fileName && this.fileName.toLowerCase().indexOf('.csv') < 0) {
      this.badFileFormat = true;
      this.clearSelectedFile();
    }
  }

  clearSelectedFile(): void {
    this.files.clear();
    if (this.file && this.file.nativeElement) {
      this.file.nativeElement.value = '';
    }
    this.fileName = '';
    this.showClearButton = false;
  }
  downloadSampleCSV(): void {
    const fields = this.sites?.length > 0 ? SiteRelatedCSVFields : LocationRelatedCSVFields;
    const data = [];
    const sampleCSVFile = this.papa.unparse({
      data,
      fields
    });
    const blob = new Blob([sampleCSVFile]);
    const a = document.createElement('a');
    //eslint-disable-next-line
    a.href = URL.createObjectURL(blob);
    a.download = 'fusion-unmanaged-devices.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }

  cancelImport(): void {
    this.uploadInProgress = false;
  }

  //eslint-disable-next-line
  async importDevicesfile() {
    this.validationErrors = [];
    this.isImportFailed = false;
    this.importFailedMsg = null;
    if (this.devicesFiles.length > 0) {
      this.uploadInProgress = true;
      this.importStatusMsg = 'Validating devices...';
      this.importProgress = 0;
      const importedFile = this.devicesFiles[0];

      const response = await this.parseData(importedFile);

      if (this.validationErrors.length > 0) {
        this.cancelImport();
      } else {
        await this.processImportedData(response);
      }

      this.fileName = null;
      if (this.validationErrors.length > 0 || this.importFailedMsg) {
        this.dialogRef.updateSize('70rem');
      }
      this.devicesFiles = null;
      this.showClearButton = false;

      if (this.file && this.file.nativeElement) {
        this.file.nativeElement.value = '';
      }
    }
  }

  //eslint-disable-next-line
  async parseData(importedFile): Promise<any> {
    return new Promise(resolve => {
      //eslint-disable-next-line
      const self = this;
      self.papa.parse(importedFile, {
        header: true,
        skipEmptyLines: true,
        encoding: 'ISO-8859-1',
        transformHeader: function (h) {
          return h.trim().toLowerCase();
        },
        //eslint-disable-next-line
        complete: function (results, $files, err?) {
          if (err) {
            //eslint-disable-next-line
            console.log('err ', err);
          }
          let filteredRecordsToImport = [];
          if (results.errors.length > 0) {
            self.validationErrors.push({ line: 0, validationMsg: 'Invalid CSV file format' });
          } else {
            filteredRecordsToImport = map(results.data, function (item) {
              for (const i in item) {
                item[i] = item[i].trim();
              }
              return item;
            });
          }
          resolve(filteredRecordsToImport);
        }
      });
    });
  }

  //eslint-disable-next-line
  async processImportedData(dataList): Promise<void> {
    this.addDevicesList = [];
    this.updateDevicesList = [];
    this.deleteDevicesList = [];
    this.updateProcessed = false;
    this.addProcessed = false;
    this.deleteProcessed = false;
    this.agentsCount = dataList.length;
    if (this.agentsCount > 0) {
      this.validationErrors = [];
      await this.validateDeviceConfigs(dataList);

      if (this.uploadInProgress === false) {
        this.notifyService.onSuccess('Import cancelled', null, { enableHtml: true });
      } else if (this.validationErrors.length === 0) {
        for (let i = 0; i < this.agentsCount; i++) {
          if (
            dataList[i].action &&
            dataList[i].action.toLowerCase() === 'delete' &&
            this.unmanagedDeviceList
          ) {
            const delDevice = this.unmanagedDeviceList.find(
              device => device.deviceData.name === dataList[i].name
            );
            if (delDevice) {
              this.deleteDevicesList.push({ id: delDevice.deviceData.id, type: 'unmanaged' });
            }
          } else {
            const obj = this.addDevicesDataMapping(dataList[i]);
            if (
              this.unmanagedDeviceList &&
              this.unmanagedDeviceList.find(function (obj) {
                return obj.deviceData.name === dataList[i].name;
              })
            ) {
              const device = this.unmanagedDeviceList.find(
                obj => obj.deviceData.name === dataList[i].name
              );
              obj['id'] = device.deviceData.id;
              obj['name'] = device.deviceData.id;
              this.updateDevicesList.push(obj);
            } else {
              this.addDevicesList.push(obj);
            }
          }
        }

        if (this.addDevicesList.length > 0) {
          this.importStatusMsg = 'Adding devices...';
          this.importProgress = 0;
          await this.addDevices();
        }
        if (this.updateDevicesList.length > 0) {
          this.importStatusMsg = 'Updating devices...';
          this.importProgress = 0;
          await this.updateDevices();
        }
        if (this.deleteDevicesList.length > 0) {
          this.importStatusMsg = 'Removing devices...';
          this.importProgress = 0;
          await this.deleteDevices();
        } else if (
          this.addDevicesList.length === 0 &&
          this.updateDevicesList.length === 0 &&
          this.deleteDevicesList.length === 0
        ) {
          this.uploadInProgress = false;
          this.isImportFailed = true;
          this.importFailedMsg = 'Devices listed in the import file are not found on the system.';
        }
        if (this.uploadInProgress === false) {
          if (!this.isImportFailed) {
            this.notifyService.onSuccess('Import cancelled', null, { enableHtml: true });
          }
        } else {
          this.processSuccessResponse();
        }
      } else {
        this.uploadInProgress = false;
        this.isImportFailed = true;
        this.importFailedMsg = 'Failed to import: Invalid configuration in "' + this.fileName + '"';
      }
    } else {
      this.uploadInProgress = false;
      this.isImportFailed = true;
      this.importFailedMsg =
        'Failed to import: device configuration not found in "' + this.fileName + '"';
    }
  }

  // a common asyn for loop iterator based on numberOfItemsPerIteration
  //
  //eslint-disable-next-line
  async asyncFor(items, callback) {
    const itemCount = items.length;
    const numIter = Math.ceil(itemCount / this.numberOfItemsPerIteration);
    for (let i = 1; i <= numIter; i++) {
      const index = i === 1 ? 0 : (i - 1) * this.numberOfItemsPerIteration;
      const length = Math.min(i * this.numberOfItemsPerIteration, itemCount);
      const promise: Promise<void> = new Promise(resolve => {
        setTimeout(() => {
          callback(index, length);
          resolve();
        }, 0);
      });
      await promise;
    }
  }

  //eslint-disable-next-line
  async validateDeviceConfigs(devicesConfig): Promise<void> {
    const deviceCount = devicesConfig.length;
    await this.asyncFor(devicesConfig, (i, len) => {
      if (
        this.uploadInProgress === true &&
        (this.validationErrors.length === 0 || deviceCount < this.numberOfItemsPerIteration)
      ) {
        this.importProgress = (i / deviceCount) * 100;
        for (i; i < len; ++i) {
          const invalidMsg = [];
          let deviceAction = '';
          if (devicesConfig[i].action) {
            deviceAction = devicesConfig[i].action.toLowerCase();
            if (deviceAction != 'delete' && deviceAction != 'include') {
              invalidMsg.push('Action keyword is invalid');
            }
          }

          if (!devicesConfig[i].name) {
            invalidMsg.push('Name is required');
          } else {
            const devices = devicesConfig.filter(function (obj) {
              return obj.name === devicesConfig[i].name;
            });
            if (devices.length > 1) {
              invalidMsg.push('Name is duplicated');
            }
          }
          if (
            deviceAction !== 'delete' &&
            !devicesConfig[i][LABELS.twampServerIP] &&
            !devicesConfig[i][LABELS.twampLightIP] &&
            !devicesConfig[i][LABELS.twampPingIP] &&
            !devicesConfig[i][LABELS.l2Loopback] &&
            !devicesConfig[i][LABELS.l3Ipv4Loopback] &&
            !devicesConfig[i][LABELS.l3Ipv6Loopback]
          ) {
            invalidMsg.push('You must define at least one reflector type or loopback');
          }

          if (devicesConfig[i][LABELS.twampServerIP] || devicesConfig[i][LABELS.twampServerPort]) {
            if (devicesConfig[i][LABELS.twampServerIP]) {
              if (
                !this.isValidIPAddress(
                  devicesConfig[i][LABELS.twampServerIP],
                  devicesConfig[i][LABELS.twampIpv6]
                )
              ) {
                invalidMsg.push('TWAMP Control Server IP is invalid');
              } else {
                const devices = devicesConfig.filter(function (obj) {
                  return (
                    (obj[LABELS.twampServerIP].trim() &&
                      obj[LABELS.twampServerIP] === devicesConfig[i][LABELS.twampServerIP]) ||
                    (obj[LABELS.twampLightIP].trim() &&
                      obj[LABELS.twampLightIP] === devicesConfig[i][LABELS.twampServerIP]) ||
                    (obj[LABELS.twampPingIP].trim() &&
                      obj[LABELS.twampPingIP] === devicesConfig[i][LABELS.twampServerIP])
                  );
                });
                if (devices.length > 1) {
                  invalidMsg.push('TWAMP Control Server IP is duplicated');
                }
                if (
                  devicesConfig[i].action.toLowerCase() !== 'delete' &&
                  this.reflectorsIpList.includes(devicesConfig[i][LABELS.twampServerIP]) &&
                  this.unmanagedDeviceList.every(
                    obj => obj.deviceData.name !== devicesConfig[i].name
                  )
                ) {
                  invalidMsg.push('TWAMP Control Server IP already exists in the inventory');
                }
              }
            } else {
              invalidMsg.push('TWAMP Control Server IP is missing');
            }

            if (devicesConfig[i][LABELS.twampServerPort]) {
              if (!this.validPort(devicesConfig[i][LABELS.twampServerPort])) {
                invalidMsg.push('TWAMP Control Server Port is invalid');
              }
            } else {
              invalidMsg.push('TWAMP Control Server Port is missing');
            }
          }

          if (devicesConfig[i][LABELS.twampLightIP] || devicesConfig[i][LABELS.twampLightPort]) {
            if (devicesConfig[i][LABELS.twampLightIP]) {
              const isvalidIP = this.isValidIPAddress(
                devicesConfig[i][LABELS.twampLightIP],
                devicesConfig[i][LABELS.twampLightIpv6]
              );
              if (!isvalidIP) {
                invalidMsg.push('TWAMP Light Reflector IP is invalid');
              } else {
                const devices = devicesConfig.filter(function (obj) {
                  return (
                    (obj[LABELS.twampServerIP].trim() &&
                      obj[LABELS.twampServerIP] === devicesConfig[i][LABELS.twampLightIP]) ||
                    (obj[LABELS.twampLightIP].trim() &&
                      obj[LABELS.twampLightIP] === devicesConfig[i][LABELS.twampLightIP]) ||
                    (obj[LABELS.twampPingIP].trim() &&
                      obj[LABELS.twampPingIP] === devicesConfig[i][LABELS.twampLightIP])
                  );
                });
                if (devices.length > 1) {
                  invalidMsg.push('TWAMP Light Reflector IP is duplicated');
                }
                if (
                  devicesConfig[i].action.toLowerCase() !== 'delete' &&
                  this.reflectorsIpList.includes(devicesConfig[i][LABELS.twampLightIP]) &&
                  this.unmanagedDeviceList.every(
                    obj => obj.deviceData.name !== devicesConfig[i].name
                  )
                ) {
                  invalidMsg.push('TWAMP Light Reflector IP already exists in the inventory');
                }
              }
            } else {
              invalidMsg.push('TWAMP Light Reflector IP is missing');
            }

            if (devicesConfig[i][LABELS.twampLightPort]) {
              if (!this.validPort(devicesConfig[i][LABELS.twampLightPort])) {
                invalidMsg.push('TWAMP Light Reflector Port is invalid');
              }
            } else {
              invalidMsg.push('TWAMP Light Reflector Port is missing');
            }
          }

          if (devicesConfig[i][LABELS.twampPingIP]) {
            if (!this.isValidIPAddress(devicesConfig[i][LABELS.twampPingIP], 'true')) {
              invalidMsg.push('Ping Reflector IP is invalid');
            } else {
              const devices = devicesConfig.filter(obj => {
                return (
                  (obj[LABELS.twampServerIP].trim() &&
                    obj[LABELS.twampServerIP] === devicesConfig[i][LABELS.twampPingIP]) ||
                  (obj[LABELS.twampLightIP].trim() &&
                    obj[LABELS.twampLightIP] === devicesConfig[i][LABELS.twampPingIP]) ||
                  (obj[LABELS.twampPingIP].trim() &&
                    obj[LABELS.twampPingIP] === devicesConfig[i][LABELS.twampPingIP])
                );
              });
              if (devices.length > 1) {
                invalidMsg.push('Ping Reflector IP is duplicated');
              }
              if (
                devicesConfig[i].action.toLowerCase() !== 'delete' &&
                this.reflectorsIpList.includes(devicesConfig[i][LABELS.twampPingIP]) &&
                this.unmanagedDeviceList.every(obj => obj.deviceData.name !== devicesConfig[i].name)
              ) {
                invalidMsg.push('Ping Reflector IP already exists in the inventory');
              }
            }
          }

          if (devicesConfig[i][LABELS.l2Loopback]) {
            if (!CommonRegEx.MAC.test(devicesConfig[i][LABELS.l2Loopback])) {
              invalidMsg.push('L2 Loopback MAC is invalid');
            } else {
              const devices = devicesConfig.filter(obj => {
                return (
                  obj[LABELS.l2Loopback].trim() &&
                  obj[LABELS.l2Loopback] === devicesConfig[i][LABELS.l2Loopback]
                );
              });
              if (devices.length > 1) {
                invalidMsg.push('L2 Loopback MAC is duplicated');
              }
            }
          }

          if (devicesConfig[i][LABELS.l3Ipv4Loopback]) {
            if (!this.isValidIPAddress(devicesConfig[i][LABELS.l3Ipv4Loopback], 'false')) {
              invalidMsg.push('L3 Loopback IPV4 is invalid');
            } else {
              const devices = devicesConfig.filter(obj => {
                return (
                  (obj[LABELS.l3Ipv4Loopback].trim() &&
                    obj[LABELS.l3Ipv4Loopback] === devicesConfig[i][LABELS.l3Ipv4Loopback]) ||
                  (obj[LABELS.l3Ipv6Loopback].trim() &&
                    obj[LABELS.l3Ipv6Loopback] === devicesConfig[i][LABELS.l3Ipv4Loopback])
                );
              });
              if (devices.length > 1) {
                invalidMsg.push('L3 Loopback IPV4 is duplicated');
              }
              if (
                devicesConfig[i].action.toLowerCase() !== 'delete' &&
                this.loopbacksIpList.includes(devicesConfig[i][LABELS.l3Ipv4Loopback]) &&
                this.unmanagedDeviceList.every(obj => obj.deviceData.name !== devicesConfig[i].name)
              ) {
                invalidMsg.push('L3 Loopback IPV4 already exists in the inventory');
              }
            }
          }

          if (devicesConfig[i][LABELS.l3Ipv6Loopback]) {
            if (!this.isValidIPAddress(devicesConfig[i][LABELS.l3Ipv6Loopback], 'true')) {
              invalidMsg.push('L3 Loopback IPV6 is invalid');
            } else {
              const devices = devicesConfig.filter(obj => {
                return (
                  obj[LABELS.l3Ipv6Loopback].trim() &&
                  obj[LABELS.l3Ipv6Loopback] === devicesConfig[i][LABELS.l3Ipv6Loopback]
                );
              });
              if (devices.length > 1) {
                invalidMsg.push('L3 Loopback IPV6 is duplicated');
              }
              if (
                devicesConfig[i].action.toLowerCase() !== 'delete' &&
                this.loopbacksIpList.includes(devicesConfig[i][LABELS.l3Ipv6Loopback]) &&
                this.unmanagedDeviceList.every(obj => obj.deviceData.name !== devicesConfig[i].name)
              ) {
                invalidMsg.push('L3 Loopback IPV6 already exists in the inventory');
              }
            }
          }

          if (
            devicesConfig[i][LABELS.latitude] &&
            !CommonRegEx.LATITUDE.test(devicesConfig[i][LABELS.latitude])
          ) {
            invalidMsg.push('Invalid Latitude');
          }

          if (
            devicesConfig[i][LABELS.longitude] &&
            !CommonRegEx.LONGITUDE.test(devicesConfig[i][LABELS.longitude])
          ) {
            invalidMsg.push('Invalid Longitude');
          }

          if (devicesConfig[i] && invalidMsg.length > 0 && this.validationErrors.length < 5) {
            this.validationErrors.push({ line: i + 2, validationMsg: invalidMsg.join(', ') });
          }
        }
      }
    });
  }

  //eslint-disable-next-line
  isValidIPAddress(address, ipV6Item) {
    const isV6 = ipV6Item ? ipV6Item.toLowerCase() === 'true' : true;
    return address ? (isV6 ? IPHelper.isValidIP(address) : IPHelper.isIPv4(address)) : false;
  }

  //eslint-disable-next-line
  validBoolean(item) {
    const value = item.toLowerCase();
    return value === 'true' || value === 'false' ? true : false;
  }

  //eslint-disable-next-line
  validInteger(item) {
    return /^(0|[1-9]\d*)$/.test(item);
  }

  //eslint-disable-next-line
  validPort(item) {
    if (this.validInteger(item)) {
      const pnum = Number(item);
      return pnum >= 1 && pnum <= 65535;
    }
    return false;
  }

  //eslint-disable-next-line
  validFrameSize(item) {
    return parseInt(item) >= 87 && parseInt(item) <= 1518;
  }

  //eslint-disable-next-line
  addDevicesDataMapping(row) {
    const finalDataObj = {};
    finalDataObj['id'] = null;
    finalDataObj['name'] = null;
    finalDataObj['tenantId'] = null;
    finalDataObj['type'] = this.defaultValue.type;
    finalDataObj['alias'] = row.name;

    const modelDataObj = {};
    modelDataObj['model'] = row.model || null;
    modelDataObj['vendor'] = row.vendor || '';
    modelDataObj['version'] = row.version || '';

    const extentionObj = {};
    const locationObj = {};
    const positionDataObj = {};

    // process Sites if sites service is present, otherwise we process Location fields
    if (this.sites?.length > 0) {
      if (row.site) {
        const siteInfo = this.sites.find(
          site => site.sitesData.name.toLowerCase() === row.site.toLowerCase()
        );
        locationObj['address'] = siteInfo?.sitesData.id || '';
        positionDataObj['lat'] = siteInfo?.sitesData.location.lat || this.defaultValue.latitude;
        positionDataObj['lon'] = siteInfo?.sitesData.location.lon || this.defaultValue.longitude;
      }
    } else {
      locationObj['address'] = row?.location || '';
      positionDataObj['lat'] = row?.latitude || this.defaultValue.latitude;
      positionDataObj['lon'] = row?.longitude || this.defaultValue.longitude;
    }

    if (positionDataObj && Object.keys(positionDataObj).length > 0) {
      locationObj['position'] = positionDataObj;
    }
    if (locationObj && Object.keys(locationObj).length > 0) {
      extentionObj['location'] = locationObj;
    }

    const capabilitiesObj = {};

    if (row[LABELS.twampServerIP] && row[LABELS.twampServerPort]) {
      const twampReflectorObj = {};
      twampReflectorObj['ipv6'] = row[LABELS.twampIpv6]
        ? row[LABELS.twampIpv6].toLowerCase()
        : this.defaultValue.twampipv6;
      twampReflectorObj['control-server-ip'] = row[LABELS.twampServerIP];
      twampReflectorObj['control-server-port'] = row[LABELS.twampServerPort];
      capabilitiesObj['twamp-reflector'] = twampReflectorObj;
    }

    if (row[LABELS.twampLightIP] && row[LABELS.twampLightPort]) {
      const twampReflectorLightObj = {};
      twampReflectorLightObj['ipv6'] = row[LABELS.twampLightIpv6]
        ? row[LABELS.twampLightIpv6].toLowerCase()
        : this.defaultValue.lightipv6;
      twampReflectorLightObj['ip'] = row[LABELS.twampLightIP] || '';
      twampReflectorLightObj['udp-port'] = row[LABELS.twampLightPort] || '';
      capabilitiesObj['twamp-light-reflector'] = twampReflectorLightObj;
    }

    if (row[LABELS.twampPingIP]) {
      const twampPingObj = {};
      twampPingObj['ip'] = row[LABELS.twampPingIP] || '';
      capabilitiesObj['twamp-ping-reflector'] = twampPingObj;
    }

    if (row[LABELS.l2Loopback]) {
      const l2LoopbackObj = {};
      l2LoopbackObj['mac'] = row[LABELS.l2Loopback];
      capabilitiesObj['l2-loopback'] = l2LoopbackObj;
    }

    if (row[LABELS.l3Ipv4Loopback]) {
      const l3Ipv4LoopbackObj = {};
      l3Ipv4LoopbackObj['ip'] = row[LABELS.l3Ipv4Loopback];
      capabilitiesObj['l3-ipv4-loopback'] = l3Ipv4LoopbackObj;
    }

    if (row[LABELS.l3Ipv6Loopback]) {
      const l3Ipv6LoopbackObj = {};
      l3Ipv6LoopbackObj['ip'] = row[LABELS.l3Ipv6Loopback];
      capabilitiesObj['l3-ipv6-loopback'] = l3Ipv6LoopbackObj;
    }

    if (capabilitiesObj && Object.keys(capabilitiesObj).length > 0) {
      extentionObj['capabilities'] = capabilitiesObj;
    }
    if (modelDataObj && Object.keys(modelDataObj).length > 0) {
      finalDataObj['model'] = modelDataObj;
    }
    if (extentionObj && Object.keys(extentionObj).length > 0) {
      finalDataObj['extension'] = extentionObj;
    }
    return finalDataObj;
  }

  getAllDevices(): void {
    this.cacheObj = this.unmanagedDevicesService.cache.subscribe(res => {
      this.unmanagedDeviceList = res;
      this.extractDeviceData();
    });
    this.cacheObj = this.sitesService.cache.subscribe(sites => {
      this.sites = sites;
    });
  }

  extractDeviceData(): any {
    // Reset collected data
    this.reflectorsIpList = [];
    this.loopbacksMacList = [];
    this.loopbacksIpList = [];

    this.unmanagedDeviceList.forEach(device => {
      const deviceData = device.deviceData;
      if (!deviceData) return;

      // Collect Reflectors/ Loopbacks MACs & IPs
      if (deviceData.capabilities) {
        Object.keys(deviceData.capabilities).forEach(key => {
          const data = deviceData.capabilities[key];
          if (data.mac) {
            this.loopbacksMacList.push(data.mac);
          }
          if (
            key === 'twamp-light-reflector' ||
            key === 'twamp-reflector' ||
            key === 'twamp-ping-reflector'
          ) {
            // Collect Reflectors IPs
            if (data.ip) {
              this.reflectorsIpList.push(data.ip);
            } else if (data['control-server-ip']) {
              this.reflectorsIpList.push(data['control-server-ip']);
            }
          } else {
            if (data.ip) {
              // Collect Loopbacks IPs
              this.loopbacksIpList.push(data.ip);
            }
          }
        });
      }
    });
  }

  async addDevices(): Promise<void> {
    try {
      for (let i = 0; i < this.addDevicesList.length; i += this.numberOfItemsPerIteration) {
        if (!this.uploadInProgress) break;
        const items = this.addDevicesList.slice(i, i + this.numberOfItemsPerIteration);
        this.importProgress =
          ((i + this.numberOfItemsPerIteration) / this.addDevicesList.length) * 100;
        await this.unmanagedDevicesService.addUnmanagedDevices(items).toPromise();
      }
      this.addProcessed = true;
    } catch (err) {
      this.processErrorResponse(err);
    }
  }

  async updateDevices(): Promise<void> {
    try {
      for (let i = 0; i < this.updateDevicesList.length; i += this.numberOfItemsPerIteration) {
        if (!this.uploadInProgress) break;
        const items = this.updateDevicesList.slice(i, i + this.numberOfItemsPerIteration);
        this.importProgress =
          ((i + this.numberOfItemsPerIteration) / this.updateDevicesList.length) * 100;
        await this.unmanagedDevicesService.updateUnmanagedDevice(items).toPromise();
      }
      this.updateProcessed = true;
    } catch (err) {
      this.processErrorResponse(err);
    }
  }

  async deleteDevices(): Promise<void> {
    try {
      for (let i = 0; i < this.deleteDevicesList.length; i += this.numberOfItemsPerIteration) {
        if (!this.uploadInProgress) break;
        const items = this.deleteDevicesList.slice(i, i + this.numberOfItemsPerIteration);
        this.importProgress =
          ((i + this.numberOfItemsPerIteration) / this.deleteDevicesList.length) * 100;
        await this.unmanagedDevicesService.deleteDevices(items).toPromise();
      }
      this.deleteProcessed = true;
    } catch (err) {
      this.processErrorResponse(err);
    }
  }

  processSuccessResponse(): void {
    const msg = this.createMessage();
    this.notifyService.onSuccess(msg, null, { enableHtml: true });
    this.dialogRef.close();
  }

  //eslint-disable-next-line
  processErrorResponse(err: any): void {
    this.notifyService.onFail(err.error.message);
    this.dialogRef.close();
  }

  createMessage(): string {
    let message = '';
    if (this.addProcessed) {
      message = message + this.addDevicesList.length + ' device(s) added<br/>';
    }
    if (this.updateProcessed) {
      message = message + this.updateDevicesList.length + ' device(s) updated<br/>';
    }
    if (this.deleteProcessed) {
      message = message + this.deleteDevicesList.length + ' device(s) deleted';
    }
    return message;
  }
}
